import { useCallback, useEffect, useMemo, useState } from 'react';
import { faGear } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Icon } from '@/components/ui/icon';
import { MutedText } from '@/components/ui/typography';
import type { CollectionCardField } from '@/lib/blocks/collection/blocks';
import {
  type CollectionButtonSettings,
  DEFAULT_COLLECTION_BUTTON_SETTINGS,
  readCollectionButtonSettings,
} from '@/lib/blocks/collection/button-settings';
import {
  getIndexCollectionVariants,
  INDEX_COLLECTION_VARIANT_PROPS_PERSISTENCE,
  readIndexCollectionContentType,
  readIndexCollectionLayoutKey,
} from '@/lib/blocks/collection/index-collection/blocks';
import type { IndexCollectionContentType } from '@/lib/blocks/collection/index-collection/registry';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';
import { BackgroundTab } from './collection/settings/background-tab';
import { ButtonTab } from './collection/settings/button-tab';
import { ContentTab } from './collection/settings/content-tab';
import { SettingsPopoverShell } from './collection/settings/settings-popover-shell';
import { SettingsTab } from './collection/settings/settings-tab';
import { useCollectionSettings } from './collection/settings/use-collection-settings';
import { BlockActionButton } from './toolbar/block-action-button';

interface IndexCollectionSettingsDialogProps {
  sectionId: string;
  block: PageBlock;
  cardTemplateBlock?: PageBlock;
  cardTemplateChildren?: PageBlock[];
  isVisible: boolean;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function stripIndexCollectionRuntimeProps(props: Record<string, unknown>): Record<string, unknown> {
  const { items: _items, variantProps: _variantProps, ...rest } = props;

  return rest;
}

function indexCollectionContentDescription(contentType: IndexCollectionContentType | undefined) {
  if (contentType === 'case_result') {
    return 'Choose which elements appear in your case results:';
  }
  if (contentType === 'blog') {
    return 'Choose which elements appear in your blog posts:';
  }
  return 'Choose which elements appear in your entries:';
}

export function IndexCollectionSettingsDialog({
  sectionId,
  block,
  cardTemplateBlock,
  cardTemplateChildren = [],
  isVisible,
  disabled,
  onOpenChange,
}: IndexCollectionSettingsDialogProps) {
  const batchUpdateBlocks = usePageEditorStore((state) => state.batchUpdateBlocks);
  const variants = getIndexCollectionVariants();
  const contentType = readIndexCollectionContentType(block);
  const persistedProps = useMemo(
    () => stripIndexCollectionRuntimeProps(block.props ?? {}),
    [block.props],
  );
  const settings = useCollectionSettings({
    sectionId,
    block,
    cardTemplateBlock,
    cardTemplateChildren,
    variants,
    variantProps: {
      ...INDEX_COLLECTION_VARIANT_PROPS_PERSISTENCE,
      scopeKey: readIndexCollectionLayoutKey(block),
    },
    getPersistedProps: stripIndexCollectionRuntimeProps,
    onOpenChange,
  });
  const [draftButtonSettings, setDraftButtonSettings] = useState<CollectionButtonSettings>(
    DEFAULT_COLLECTION_BUTTON_SETTINGS,
  );

  useEffect(() => {
    if (!settings.open) return;
    setDraftButtonSettings(readCollectionButtonSettings(persistedProps));
  }, [settings.open, persistedProps]);

  const handleFieldsChange = (nextFields: CollectionCardField[]) => {
    settings.setDraftFields(nextFields);

    const batchedUpdates: Array<{ blockId: string; updates: Partial<PageBlock> }> = [];

    nextFields.forEach((field, index) => {
      if (field.blockId) {
        batchedUpdates.push({
          blockId: field.blockId,
          updates: { visible: field.enabled, position: index + 1 },
        });
      }
    });

    if (batchedUpdates.length > 0) {
      batchUpdateBlocks(sectionId, batchedUpdates);
    }
  };

  const handleButtonSettingsChange = useCallback(
    (patch: Partial<CollectionButtonSettings>) => {
      setDraftButtonSettings((current) => {
        const next = { ...current, ...patch };
        settings.patchBlockProps({ buttonSettings: next });
        return next;
      });
    },
    [settings.patchBlockProps],
  );

  const showButtonTab = settings.draftFields.some(
    (field) => field.kind === 'button' && field.enabled,
  );

  return (
    <SettingsPopoverShell
      open={settings.open}
      onOpenChange={settings.handleOpenChange}
      title="Collection Settings"
      showLayoutTab={false}
      trigger={
        <BlockActionButton
          icon={<Icon icon={faGear} />}
          label="Collection Settings"
          srLabel="Edit collection settings"
          showLabel
          isVisible={isVisible}
          disabled={disabled}
          className={cn(settings.open && 'bg-primary/7')}
          onPointerDown={(event) => event.stopPropagation()}
        />
      }
      layoutContent={null}
      contentContent={
        settings.draftFields.length > 0 ? (
          <ContentTab
            fields={settings.draftFields}
            onFieldsChange={handleFieldsChange}
            disabledImageField={false}
            fieldGroups={contentType === 'blog' ? [{ slots: ['publishedAt', 'authors'] }] : []}
            description={indexCollectionContentDescription(contentType)}
          />
        ) : (
          <MutedText>No fields defined for this collection.</MutedText>
        )
      }
      backgroundContent={
        <BackgroundTab
          variantProps={settings.activeVariantProps}
          onVariantPropsChange={settings.handleVariantPropsChange}
        />
      }
      settingsContent={
        settings.activeVariantEntry ? (
          <SettingsTab
            variantProps={settings.activeVariantProps}
            renderer={settings.activeVariantEntry.renderer}
            cardAlign={settings.draftCardAlign}
            onVariantPropsChange={settings.handleVariantPropsChange}
            onCardAlignChange={settings.handleCardAlignChange}
            showBackgroundPosition={contentType === 'blog'}
            showLayoutControls={false}
            showImagePosition={settings.draftFields.some(
              (field: CollectionCardField) => field.kind === 'image',
            )}
            imageAspect={settings.imageAspect}
            onImageAspectChange={settings.handleImageAspectChange}
          />
        ) : (
          <MutedText>No settings available.</MutedText>
        )
      }
      buttonContent={
        showButtonTab ? (
          <ButtonTab
            buttonSettings={draftButtonSettings}
            onButtonSettingsChange={handleButtonSettingsChange}
          />
        ) : undefined
      }
    />
  );
}
